import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { BasicScrollView } from "@/components/BasicScrollView";
import { Pressable, ScrollView, StyleSheet, TextInput as RNTextInput, View } from 'react-native';
import { ArrowIcon, Button as Button95, Checkbox, Hourglass, NumberInput, Panel, Text, TextInput } from 'react95-native-rabbl';
import Head from 'expo-router/head';
import { CustomText } from '@/components/CustomText';
import Footer from '@/components/Footer';
import * as AppUtilities from "@/utilities/AppUtilities";
import * as AppUtils from '@/utilities/AppUtils';
import * as AppConstants from "@/utilities/AppConstants";
import * as AppConsts from "@/utilities/AppConsts";
import { useForm, Controller, Resolver } from "react-hook-form";
import { AppContext } from '@/components/AppContext';
import { CommunityMembership } from "@/types/community_membership";
import { RelativePathString, useLocalSearchParams, useRouter } from 'expo-router';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { setData } from '@/state/user';
import { RangeSlider } from '@react-native-assets/slider';
import { Meta } from '@/components/Meta';
import { AppText } from '@/components/AppText';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    paddingTop: 10,
    gap: 20,
    backgroundColor: 'transparent',
    alignItems: 'center',
  },
  formElement: {
    width: AppConsts.BASIC_MINIMUM_WIDTH,
  },
  buttonWrapper: {
    flexDirection: 'row',
  },
  panel: {
    padding: 20,
    gap: 20,
    alignItems: 'center',
  },
  numericWrapper: {
    flexDirection: 'row',
    gap: 5,
  },
  numeric: {
    width: 80,
  },
});

export default function DesiredAges() {
  const domain = useSelector((state: RootState) => state.user.domain);
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const router = useRouter();
  const [values, setValues] = useState<[number, number]>([AppConsts.MIN_AGE, AppConsts.MAX_AGE]);
  const [ageMin, setAgeMin] = useState(AppConsts.MIN_AGE);
  const [ageMax, setAgeMax] = useState(AppConsts.MAX_AGE);
  const dispatch = useDispatch();
  const { edit } = useLocalSearchParams<{ edit?: string }>();

  const onSubmit = useCallback(async() => {
    try {
      showSpinner();

      if (ageMin < AppConsts.MIN_AGE || ageMax > AppConsts.MAX_AGE || ageMin > ageMax) {
        throw new Error('Invalid ages');
      }

      const response = await AppUtilities.fetchAuthorized(
        "DesiredAges.onSubmit",
        "/rabbl/api/set_desired_ages",
        "POST",
        {
          domain: domain,
          age_min: ageMin,
          age_max: ageMax,
        }
      );
      const membership: CommunityMembership = ((response as any).body as any).community_membership;
      dispatch(setData(response));
      if (edit && edit.length > 0 && router.canGoBack()) {
        router.back();
      } else {
        router.navigate(AppUtils.getProfileCreateNextPageLink(membership) as RelativePathString);
      }

    } catch (e) {
      AppUtils.showErrorToast(AppUtilities.getErrors(e), () => onSubmit(), true);
      AppUtilities.handleException(e, "DesiredAges.onSubmit");
    } finally {
      hideSpinner();
    }
  }, [domain, ageMin, ageMax]);

  const handleTemporaryAgeValuesChange = (newValues: number | [number, number]) => {
    const rangeValues = newValues as [number, number];
    setValues([Math.trunc(rangeValues[0]), Math.trunc(rangeValues[1])]);
  };

  const handleFinalAgeValuesChange = (newValues: number | [number, number]) => {
    const rangeValues = newValues as [number, number];
    setAgeMin(Math.trunc(rangeValues[0]));
    setAgeMax(Math.trunc(rangeValues[1]));
  };

  const thumb = useCallback(AppUtils.RangeSliderThumb, []);

  return (
    <BasicScrollView center={true}>
      <Meta title={"Desired ages"} />
            
      <Panel variant='raised' elevation={4} style={{ width: AppConsts.BASIC_MINIMUM_WIDTH + 60}}>
        <View style={styles.panel}>

          <AppText>What ages are you open to?</AppText>

          <CustomText>{values[0]} - {values[1]}</CustomText>
          
          {
            // https://github.com/Sharcoux/slider#range-slider
          }
          <RangeSlider
            minimumValue={AppConsts.MIN_AGE}
            maximumValue={AppConsts.MAX_AGE}
            range={values}
            step={1}
            onValueChange={handleTemporaryAgeValuesChange}
            onSlidingComplete={handleFinalAgeValuesChange}
            style={AppConsts.STYLES.rangeSlider}
            CustomThumb={thumb}
            outboundColor={AppConsts.COLOR3}
            inboundColor={AppConsts.COLOR1}
          />

          <View style={AppConstants.STYLES.padding2} />

          <View style={styles.buttonWrapper}>
            <Button95 onPress={onSubmit}>{ edit ? "Save" : "Next" }</Button95>
          </View>
        </View>
      </Panel>
      
      <Footer />
    </BasicScrollView>
  );
}
